import React, { useState, useEffect, CSSProperties } from 'react';
import { NodeToolbar, Position } from '@xyflow/react';
import { ButtonGroup, Button, Popover, Menu, MenuItem, PopoverInteractionKind, Position as BPPosition } from '@blueprintjs/core';
import { rootStore, useAppStore } from '../../stores/UnifiedStoreProvider';
import styles from './NodeToolbar.module.css';
import { observer } from 'mobx-react-lite';

type PriorityLevel = 'low' | 'medium' | 'high';

type MindmapNodeToolbarProps = {
  nodeId: string;
  onPriorityChange?: (priority: PriorityLevel) => void;
  onNecessaryChange?: (isNecessary: boolean) => void;
  onDeleteNode?: (deleteChildren: boolean) => void;
  initialPriority?: PriorityLevel;
  initialNecessary?: boolean;
};

const MindmapNodeToolbar = observer(({
  nodeId,
  onPriorityChange = () => {},
  onNecessaryChange = () => {},
  onDeleteNode = () => {},
  initialPriority = 'medium',
  initialNecessary = false,
}: MindmapNodeToolbarProps) => {
  const appStore = useAppStore();
  const isDark = appStore.theme.isDark;
  
  const [priority, setPriority] = useState<PriorityLevel>(initialPriority);
  const [isNecessary, setIsNecessary] = useState<boolean>(initialNecessary);

  useEffect(() => {
    setPriority(initialPriority);
  }, [initialPriority]);

  useEffect(() => {
    setIsNecessary(initialNecessary);
  }, [initialNecessary]);

  const handlePriorityChange = (newPriority: PriorityLevel) => {
    setPriority(newPriority);
    onPriorityChange(newPriority);
    
    // Update in MobX store optimistically
    if (nodeId && rootStore.mindmap) {
      rootStore.mindmap.updateNodeOptimistically(nodeId, { priority: newPriority });
    }
  };

  const handleNecessaryChange = () => {
    const newValue = !isNecessary;
    setIsNecessary(newValue);
    onNecessaryChange(newValue);
    
    // Update in MobX store optimistically
    if (nodeId && rootStore.mindmap) {
      rootStore.mindmap.updateNodeOptimistically(nodeId, { isNecessary: newValue });
    }
  };

  const handleDelete = (deleteChildren: boolean) => {
    onDeleteNode(deleteChildren);
  };

  const DeleteMenu = (
    <Menu className={isDark ? 'bp5-dark' : ''}>
      <MenuItem
        text="Delete node only"
        icon="trash"
        onClick={() => handleDelete(false)}
      />
      <MenuItem
        text="Delete node and all children"
        icon="graph-remove"
        intent="danger"
        onClick={() => handleDelete(true)}
      />
    </Menu>
  );

  // Switch styles
  const getSwitchStyles = () => {
    return {
      container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '8px',
      } as CSSProperties,
      track: {
        position: 'relative' as 'relative',
        width: '40px',
        height: '20px',
        backgroundColor: isNecessary ? '#3F8AE2' : '#e0e0e0',
        borderRadius: '20px',
        transition: 'background-color 0.2s',
        cursor: 'pointer',
      } as CSSProperties,
      thumb: {
        position: 'absolute' as 'absolute',
        left: isNecessary ? '22px' : '2px',
        top: '2px',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        transition: 'left 0.2s',
      } as CSSProperties,
    };
  };
  
  const switchStyles = getSwitchStyles();

  return (
    <NodeToolbar className="nodrag" position={Position.Top} offset={10}>
      <div 
        className={`${styles.toolbar} ${isDark ? 'bp5-dark' : ''}`}
        data-theme={isDark ? 'dark' : 'light'}
        style={{
          backgroundColor: isDark ? '#2f343c' : '#ffffff',
          color: isDark ? '#f6f7f9' : '#1f2937',
          borderColor: isDark ? '#5f6b7c' : '#d1d5db'
        }}
      >
        <div className={styles.sliderContainer}>
          <span className={styles.label} style={{ color: isDark ? '#738091' : '#6b7280' }}>Priority:</span>
          <div className={styles.slider} style={{ backgroundColor: isDark ? '#1c2127' : '#f3f4f6' }}>
            <button 
              className={`${styles.sliderButton} ${priority === 'low' ? styles.active : ''}`}
              onClick={() => handlePriorityChange('low')}
              aria-label="Low priority"
              title="Low priority"
              style={{
                color: priority === 'low' && isDark ? '#72ca9b' : undefined,
                backgroundColor: priority === 'low' && isDark ? '#383e47' : undefined
              }}
            >
              Low
            </button>
            <button 
              className={`${styles.sliderButton} ${priority === 'medium' ? styles.active : ''}`}
              onClick={() => handlePriorityChange('medium')}
              aria-label="Medium priority"
              title="Medium priority"
              style={{
                color: priority === 'medium' && isDark ? '#fbb360' : undefined,
                backgroundColor: priority === 'medium' && isDark ? '#383e47' : undefined
              }}
            >
              Medium
            </button>
            <button 
              className={`${styles.sliderButton} ${priority === 'high' ? styles.active : ''}`}
              onClick={() => handlePriorityChange('high')}
              aria-label="High priority"
              title="High priority"
              style={{
                color: priority === 'high' && isDark ? '#fa999c' : undefined,
                backgroundColor: priority === 'high' && isDark ? '#383e47' : undefined
              }}
            >
              High
            </button>
          </div>
        </div>
        
        <div style={switchStyles.container}>
          <span className={styles.label} style={{ color: isDark ? '#738091' : '#6b7280' }}>
            {isNecessary ? "Necessary" : "Sufficient"}
          </span>
          <div 
            onClick={handleNecessaryChange}
            style={switchStyles.track}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleNecessaryChange();
                e.preventDefault();
              }
            }}
          role="switch"
          aria-checked={isNecessary}
          aria-label="Mark as necessary"
          >
            <div style={switchStyles.thumb} />
          </div>
        </div>
        
        <div style={{ marginTop: '12px' }}>
          <ButtonGroup>
            <Button 
              icon="trash" 
              intent="danger" 
              minimal={true} 
              onClick={() => handleDelete(false)}
              title="Delete node"
            />
            <Popover 
              content={DeleteMenu} 
              position={BPPosition.BOTTOM}
              interactionKind={PopoverInteractionKind.CLICK}
              popoverClassName={isDark ? 'bp5-dark' : ''}
            >
              <Button 
                icon="caret-down" 
                intent="danger" 
                minimal={true}
                title="Delete options"
              />
            </Popover>
          </ButtonGroup>
        </div>
      </div>
    </NodeToolbar>
  );
});

export default MindmapNodeToolbar;